import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, ChevronRight, RefreshCw, Loader2, ChevronDown, Users, TrendingUp, LogOut } from 'lucide-react'
import { mandatesApi } from '../services/api'
import { SSO_DASHBOARD_URL, useAuthStore } from '../store/authStore'

export default function CompanyDashboard() {
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)
  const growthpalToken = useAuthStore((state) => state.growthpalToken)
  // Mandates state
  const [mandates, setMandates] = useState([])
  const [selectedMandate, setSelectedMandate] = useState(null)
  const [loadingMandates, setLoadingMandates] = useState(false)

  // Shortlist state
  const [companies, setCompanies] = useState([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)

  // Company details being fetched
  const [fetchingDetails, setFetchingDetails] = useState(null)

  const [error, setError] = useState(null)

  useEffect(() => {
    // Check if authenticated - check both state and localStorage
    const hasToken = growthpalToken || localStorage.getItem('growthpal_token')
    if (!hasToken) {
      navigate('/', { replace: true })
      return
    }
    loadMandates()
  }, [growthpalToken])

  useEffect(() => {
    if (selectedMandate) {
      loadShortlist(selectedMandate.id)
    }
  }, [selectedMandate])

  const loadMandates = async () => {
    setLoadingMandates(true)
    setError(null)
    try {
      const data = await mandatesApi.list()
      setMandates(data)
      if (data.length > 0 && !selectedMandate) {
        setSelectedMandate(data[0])
      }
    } catch (err) {
      if (err.message === 'Session expired' || err.message === 'Unauthorized') {
        return
      }
      setError('Failed to load mandates: ' + err.message)
    } finally {
      setLoadingMandates(false)
    }
  }

  const loadShortlist = async (mandateId) => {
    setLoadingCompanies(true)
    setError(null)
    try {
      const data = await mandatesApi.getShortlist(mandateId)
      setCompanies(data)
    } catch (err) {
      console.error('Failed to load shortlist:', err)
      // API client handles token refresh automatically
      if (err.message === 'Session expired' || err.message === 'Unauthorized') {
        return
      }
      setError('Failed to load shortlisted companies: ' + err.message)
      setCompanies([])
    } finally {
      setLoadingCompanies(false)
    }
  }

  const handleCompanyClick = async (company) => {
    if (!company.profileId) {
      setError('Company profile ID not found')
      return
    }

    setFetchingDetails(company.id)
    try {
      const details = await mandatesApi.getCompanyDetails(selectedMandate.id, company.profileId)

      sessionStorage.setItem('selectedCompany', JSON.stringify({
        ...details,
        mandateId: selectedMandate.id,
        mandateName: selectedMandate.name
      }))

      navigate(`/companies/${company.profileId}`)
    } catch (err) {
      console.error('Failed to fetch company details:', err)
      // API client handles token refresh automatically
      if (err.message === 'Session expired' || err.message === 'Unauthorized') {
        return
      }
      setError('Failed to fetch company details: ' + err.message)
    } finally {
      setFetchingDetails(null)
    }
  }

  const handleLogout = () => {
   window.location.href = `${SSO_DASHBOARD_URL}?redirectTo=${encodeURIComponent(window.location.href)}`
  }

  // Loading state
  if (loadingMandates && mandates.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-neutral-200 rounded w-48" />
          <div className="h-12 bg-neutral-200 rounded w-full max-w-md" />
          <div className="h-64 bg-neutral-200 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Active Mandates</h1>
          <p className="text-neutral-500 text-sm mt-1">Select a mandate and company to analyze</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedMandate(null)
              loadMandates()
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          {error}
        </div>
      )}

      {/* Mandate Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Select Mandate
        </label>
        <div className="relative max-w-md">
          <select
            value={selectedMandate?.id || ''}
            onChange={(e) => {
              const mandate = mandates.find(m => m.id === e.target.value)
              setSelectedMandate(mandate)
            }}
            className="w-full h-11 px-4 pr-10 bg-white border border-neutral-200 rounded-lg text-sm appearance-none cursor-pointer focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          >
            <option value="">Select a mandate...</option>
            {mandates.map((mandate) => (
              <option key={mandate.id} value={mandate.id}>
                {mandate.name} {mandate.client ? `(${mandate.client})` : ''}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
        </div>
      </div>

      {/* Stats Overview */}
      {selectedMandate && companies.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-neutral-200 p-4">
            <div className="flex items-center gap-2 text-neutral-500 text-sm mb-1">
              <Users className="w-4 h-4" />
              Shortlisted
            </div>
            <p className="text-2xl font-semibold text-neutral-900">{companies.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-neutral-200 p-4">
            <div className="text-neutral-500 text-sm mb-1">Mandate</div>
            <p className="text-lg font-medium text-neutral-900 truncate">{selectedMandate.name}</p>
          </div>
        </div>
      )}

      {/* Companies List */}
      {loadingCompanies ? (
        <div className="bg-white rounded-lg border border-neutral-200 p-12 text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-3" />
          <p className="text-neutral-500">Loading shortlisted companies...</p>
        </div>
      ) : selectedMandate && companies.length > 0 ? (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Sector
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Added
                </th>
                <th className="px-6 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {companies.map((company) => (
                <tr
                  key={company.id}
                  onClick={() => handleCompanyClick(company)}
                  className={`hover:bg-neutral-50 cursor-pointer transition-colors ${
                    fetchingDetails === company.id ? 'bg-primary-50' : ''
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-neutral-400" />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">{company.companyName}</p>
                        {company.website && (
                          <p className="text-sm text-neutral-500 truncate max-w-xs">
                            {company.website.replace(/^https?:\/\//, '')}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {company.sector ? (
                      <span className="px-2.5 py-1 bg-primary-50 text-primary-700 text-sm rounded-md">
                        {company.sector}
                      </span>
                    ) : (
                      <span className="text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-sm rounded-md">
                      {company.status || 'Shortlisted'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-500">
                    {company.addedAt ? new Date(company.addedAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {fetchingDetails === company.id ? (
                      <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-neutral-400" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : selectedMandate ? (
        <div className="bg-white rounded-lg border border-neutral-200 p-12 text-center">
          <Building2 className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500">No companies shortlisted for this mandate</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-neutral-200 p-12 text-center">
          <TrendingUp className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500">Select a mandate to view shortlisted companies</p>
        </div>
      )}
    </div>
  )
}
